import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { X } from "lucide-react";
import { useStore } from "../store/StoreContext";
import { img } from "../lib/format";

import EnquiryQuantity from "./EnquiryQuantity";
import CatalogueDetails from "./CatalogueDetails";
import ExportPrice from "./ExportPrice";
import useDialogFocus from "../lib/useDialogFocus";
import { productPresentation } from "../lib/productPresentation";

export default function QuickView() {
  const { quickView, setQuickView, productBySlug, addToCart } = useStore();
  const [qty, setQty] = useState("");
  const [activeImage, setActiveImage] = useState(0);
  const reduced = useReducedMotion();
  const product = quickView ? productBySlug(quickView) : null;
  const presentation = productPresentation(product || {});
  const dialogRef = useDialogFocus(!!product, () => setQuickView(null));

  useEffect(() => { setQty(""); setActiveImage(0); }, [quickView]);

  return (
    <AnimatePresence>
      {product && (
        <>
          <motion.div
            data-testid="quick-view-backdrop"
            initial={reduced ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduced ? 0 : 0.2 }}
            className="fixed inset-0 bg-ink/40 backdrop-blur-[2px] z-[80]"
            onClick={() => setQuickView(null)}
          />
          <motion.div
            data-testid="quick-view-modal"
            data-lenis-prevent
            ref={dialogRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-label="Product quick view"
            initial={reduced ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduced ? 0 : 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="quick-view-panel fixed inset-x-4 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:w-[calc(100vw-2rem)] sm:max-w-[780px] bg-white rounded-[28px] z-[90] overflow-y-auto overscroll-contain shadow-2xl"
          >
            <button
              data-testid="quick-view-close"
              onClick={() => setQuickView(null)}
              className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white shadow z-10 flex items-center justify-center hover:text-brass transition-colors duration-200"
              aria-label="Close quick view"
            >
              <X className="w-5 h-5" strokeWidth={1.25} />
            </button>
            <div className="grid sm:grid-cols-2">
              <div className="bg-beige self-start">
                <div className="aspect-square flex items-center justify-center p-10">
                  {presentation.images.length > 0 && <img src={img(presentation.images[activeImage] || presentation.images[0])} alt={presentation.alt} decoding="async" className="w-full h-full object-contain cutout-shadow" />}
                </div>
                {presentation.images.length > 1 && <div className="flex flex-wrap justify-center px-3 pb-3" aria-label="Product images">
                  {presentation.images.map((src, i) => <button key={src} type="button" onClick={() => setActiveImage(i)} aria-label={`Show image ${i + 1}`} aria-pressed={activeImage === i} className="w-11 h-11 rounded-full flex items-center justify-center"><span aria-hidden="true" className={`w-2 h-2 rounded-full ${activeImage === i ? "bg-ink" : "bg-ink/25"}`} /></button>)}
                </div>}
              </div>
              <div className="p-7 sm:p-10 flex flex-col text-center sm:text-left">
                <p className="eyebrow mb-2">{product.category}</p>
                <h3 className="text-[26px] font-light text-brass">{product.name}</h3>
                <p className="mt-1 text-[11px] text-mocha">Crafted by <span className="brand-name underline">S H &amp; SONS ®</span></p>
                <p className="mt-4 text-[12px] text-mocha">{product.collections?.includes("christmas-2027") ? "Christmas Collection 2027 · Customisation available" : "Design preview · final selection pending"}</p>
                <CatalogueDetails product={product} dimensions className="mt-4" />
                <ExportPrice product={product} />
                <p className="mt-5 text-[13px] text-charcoal/80 leading-relaxed whitespace-pre-line line-clamp-4">{presentation.summary || "Share your preferred materials, finishes and sourcing requirements. Final specifications are confirmed on enquiry."}</p>
                {presentation.attributes.length > 0 && <dl className="mt-4 text-[12px] leading-relaxed space-y-2">
                  {presentation.attributes.map(([label, value]) => <div key={label}><dt className="text-mocha">{label}</dt><dd className="text-charcoal whitespace-pre-line break-words">{value}</dd></div>)}
                </dl>}

                <div className="mt-6 flex flex-col gap-4">
                  <EnquiryQuantity value={qty} onChange={setQty} id="quick-view-quantity" />
                  <p className="text-[11px] text-mocha">MOQ 500 pieces. Quantities are confirmed with our team.</p>
                  <button
                    data-testid="quick-view-add-to-enquiry"
                    onClick={() => { addToCart(product, qty); setQuickView(null); }}
                    className="pill-dark min-h-11 flex-1 py-3 uppercase tracking-[0.2em] text-[11px]"
                  >
                    Add to enquiry
                  </button>
                </div>

                <Link
                  to={`/product/${product.slug}`}
                  onClick={() => setQuickView(null)}
                  data-testid="quick-view-full-details"
                  className="mt-6 min-h-11 flex items-center justify-center sm:justify-start text-[11px] uppercase tracking-[0.2em] text-mocha hover:text-ink transition-colors duration-200"
                >
                  View full details
                </Link>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
